import { useCallback, useState, type ReactElement } from 'react'
import { Page } from '@components'
import { getNewDummyGame, useAddGame, type GameTypeDefinition } from '@front/model'
import { Switch } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { type UnsavedGame, type GameType } from '@terros/common'
import { CloneGame } from './CloneGame'
import { type PromiseVoidFunction } from './CompetitionTypeCard'
import { TypeSelector } from './CompetitionTypeSelector'

function CompetitionAddPage(): ReactElement {
  const [selectedType, setSelectedType] = useState<GameType | null>(null)
  const { add, isAdding, error: addError } = useAddGame()
  const navigate = useNavigate()

  const [shouldClone, { toggle }] = useDisclosure(false)

  const handleAddNew = useCallback(
    async (type: GameType) => {
      try {
        const initialGame: UnsavedGame = getNewDummyGame(type)
        const game = await add(initialGame)
        navigate({ to: `/competition/${game.gameId}/participant` })
      } catch (error) {
        console.error('Error adding competition:', error)
      }
    },
    [add, navigate]
  )

  const handleSelect = useCallback(
    (def: Pick<GameTypeDefinition, 'type'>): PromiseVoidFunction => {
      if (shouldClone) {
        return async () => {
          setSelectedType(def.type)
        }
      }
      return async () => {
        await handleAddNew(def.type)
      }
    },
    [setSelectedType, shouldClone, handleAddNew]
  )

  return (
    <Page>
      <Page.Heading back title='New Competition' />
      <Page.Content scrollArea>
        {selectedType ? (
          <CloneGame clearType={() => setSelectedType(null)} type={selectedType} />
        ) : (
          <>
            <TypeSelector handleSelect={handleSelect} />
            <Switch checked={shouldClone} label='Copy from past competitions' onChange={toggle} />
          </>
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/competition_/add/')({
  component: CompetitionAddPage,
})
